import { withErrorBoundary } from 'app/components/error-boundary';
import { ShowErrors } from 'app/components/show-errors';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useNavigationOptions } from 'app/hooks/navigation';
import { useProfile, useProfileShares } from 'app/network/profile';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import { createTheme as createDSTheme } from 'app/shopify';
import type { ProfileShare } from 'app/types/profile';
import { ShareMemberActionsSheet } from './share-member-actions-sheet';
import type { NavigationScreen } from 'app/types/navigation';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import { ShareHistoryRow } from './share-components';

const theme = createDSTheme();
type ShareRole = 'all' | 'lender' | 'borrower';

const ProfileShares: NavigationScreen<'profile-shares'> = ({ navigation, route }) => {
  const { t } = useTranslation('neighbour-profile');
  const [role, setRole] = useState<ShareRole>('all');
  const [selectedMember, setSelectedMember] = useState<ProfileShare['counterpart'] | null>(null);
  const profile = useProfile(route.params.userId);
  const shares = useProfileShares(route.params.userId, { scope: route.params.scope, role, take: 100 });
  const subjectName = [profile.data?.firstName, profile.data?.lastName].filter(Boolean).join(' ');
  useNavigationOptions(navigation, {
    headerTitle: () => (
      <Text fontWeight="700" variant="bodyBase">
        {subjectName}
      </Text>
    ),
  });
  const filters = [
    {
      value: 'all' as const,
      label: t('shares.filters.all', {
        count: (shares.data?.summary.lent ?? 0) + (shares.data?.summary.borrowed ?? 0),
      }),
    },
    { value: 'lender' as const, label: t('shares.filters.lent', { count: shares.data?.summary.lent ?? 0 }) },
    { value: 'borrower' as const, label: t('shares.filters.borrowed', { count: shares.data?.summary.borrowed ?? 0 }) },
  ];
  const headingKey =
    role === 'lender'
      ? 'shares.lentTitle'
      : role === 'borrower'
        ? 'shares.borrowedTitle'
        : route.params.scope === 'common'
          ? 'shares.commonTitle'
          : 'shares.allTitle';
  return (
    <Page paddingStyle={{ paddingTop: 8, paddingBottom: 24, paddingLeft: 16, paddingRight: 16 }}>
      <Row gap="spacing.general.x2" style={{ marginHorizontal: -16, paddingHorizontal: 16, marginBottom: 16 }}>
        {filters.map((filter) => {
          const active = role === filter.value;
          return (
            <Pressable
              key={filter.value}
              accessibilityRole="tab"
              accessibilityState={{ selected: active }}
              onPress={() => setRole(filter.value)}
              style={{
                borderRadius: 18,
                paddingVertical: 8,
                paddingHorizontal: 10,
                backgroundColor: active
                  ? theme.colors['surface.inverse.default']
                  : theme.colors['surface.tertiary-neutral'],
              }}
            >
              <Text color={active ? 'text.inverse.primary' : 'text.primary'} fontWeight="600" variant="bodySmall">
                {filter.label}
              </Text>
            </Pressable>
          );
        })}
      </Row>
      <Text fontWeight="700" variant="bodyBase" style={{ marginBottom: 8 }}>
        {t(headingKey, { name: subjectName })}
      </Text>
      <Column gap="spacing.general.x1">
        {shares.data?.results.map((share) => (
          <ShareHistoryRow
            key={share.id}
            share={share}
            onMore={() => setSelectedMember(share.counterpart)}
            onPress={() => navigateToListing(navigation, share.listing.id)}
          />
        ))}
      </Column>
      {shares.data?.results.length === 0 ? (
        <Text color="text.tertiary" textAlign="center" style={{ marginTop: 40 }}>
          {t('shares.empty')}
        </Text>
      ) : null}
      {selectedMember ? (
        <ShareMemberActionsSheet
          member={selectedMember}
          navigation={navigation}
          onClose={() => setSelectedMember(null)}
        />
      ) : null}
      <ShowErrors error={shares.error ?? profile.error} />
    </Page>
  );
};

export const ProfileSharesScreen = withErrorBoundary(ProfileShares);
